import { ResponsiveGameCollection } from 'config/types/game'
import { BetDetails } from 'config/types/profile'
import useDebounce from 'hooks/useDebounce'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BaseResponse, HunnyRequest, Paging } from 'services/types'
import styled, { keyframes } from 'styled-components'
import Box from 'UIKit/Box/Box'
import { openEffectMixin } from 'UIKit/OpenEffect'
import SectionTitle from 'views/Home/components/SectionTitle'
import useDisplayLiveBet from '../../LiveBet/hooks/useDisplayLiveBet'
import { LIVE_WIN_ANIMATION_DURATION, LIVE_WIN_MAX_ITEM, LIVE_WIN_PAUSE_ANIMATION_DURATION } from '../config'
import { StyledFlexContainer, StyledFlexItems } from '../style'
import LiveWinBadge from './LiveWinBadge'
import LiveWinCard from './LiveWinCard'

interface LiveWinStreamProps extends ResponsiveGameCollection {
  fetchFn: () => HunnyRequest<BaseResponse<Paging<BetDetails>>>
  validateBet: (bet: BetDetails) => boolean
}

const LiveWinStream: React.FC<LiveWinStreamProps> = ({ fetchFn, validateBet, col }) => {
  const [isInview, setIsInview] = useState(true)
  const [pendingCount, setPendingCount] = useState(0)
  const [scrollLeft, setScrollLeft] = useState(0)
  const [isPaused, setIsPaused] = useState(false)

  const containerRef = useRef<HTMLDivElement>(null)
  const previousScrollLeftRef = useRef(1)

  const isInviewDebounced = useDebounce(isInview, 5000)

  const { t } = useTranslation()

  useEffect(() => {
    let observer: IntersectionObserver

    if (containerRef?.current) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const { isIntersecting } = entry
            setIsInview(isIntersecting)
          })
        },
        {
          root: null,
          rootMargin: '100px',
          threshold: 0,
        },
      )
      observer.observe(containerRef.current)
    }

    return () => {
      if (observer) observer.disconnect()
    }
  }, [])

  useEffect(() => {
    if (!isInview && !isInviewDebounced) {
      handlePauseStream()
    } else {
      handlePlayStream()
    }
  }, [isInview, isInviewDebounced])

  useEffect(() => {
    if (scrollLeft === 0 && previousScrollLeftRef.current > 0) {
      handlePlayStream()
    } else {
      handlePauseStream()
    }
  }, [scrollLeft])

  const handlePendingCount = useCallback(() => {
    setPendingCount((preState) => preState + 1)
  }, [])

  const liveBet = useDisplayLiveBet({
    validateBet,
    initBetsFn: fetchFn,
    onNewPendingBet: handlePendingCount,
    config: {
      animationDuration: LIVE_WIN_ANIMATION_DURATION,
      limit: LIVE_WIN_MAX_ITEM,
      pauseAnimationDuration: LIVE_WIN_PAUSE_ANIMATION_DURATION,
    },
  })

  const handlePauseStream = useCallback(() => {
    setIsPaused(true)
    liveBet.pause()
  }, [])

  const handlePlayStream = useCallback(() => {
    setIsPaused(false)
    liveBet.play()
    setPendingCount(0)
  }, [])

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const container = e.target as HTMLDivElement
      previousScrollLeftRef.current = scrollLeft
      setScrollLeft(container.scrollLeft)
    },
    [scrollLeft],
  )

  const handleMouseLeaveOrTouchEnd = () => {
    if (scrollLeft === 0) {
      handlePlayStream()
    }
  }

  return (
    <Box>
      <SectionTitle
        title={t('Live Wins')}
        scrollRef={containerRef}
        mb="16px"
        leftButton={{
          badgeItem: <LiveWinBadge liveWinAmount={pendingCount} />,
        }}
        rightButton={{
          callback: handlePauseStream,
        }}
      />
      <WinContainer
        onMouseEnter={handlePauseStream}
        onMouseLeave={handleMouseLeaveOrTouchEnd}
        onTouchStart={handlePauseStream}
        onTouchEnd={handleMouseLeaveOrTouchEnd}
      >
        {liveBet.isFetching ? (
          <StyledFlexContainer row={1} gap={[4, 4, 6]} col={col} itemCount={10}>
            <StyledFlexItems>
              {Array(10)
                .fill(0)
                .map((_, index) => (
                  <Box key={`skeleton-${index}`}>
                    <SkeletonCard />
                  </Box>
                ))}
            </StyledFlexItems>
          </StyledFlexContainer>
        ) : liveBet.bets.length === 0 ? (
          <NoWinsMessage>{t('No recent wins to display')}</NoWinsMessage>
        ) : (
          <StyledFlexContainer row={1} gap={[4, 4, 6]} col={col} itemCount={liveBet.bets.length}>
            <StyledFlexItems ref={containerRef} onScroll={handleScroll}>
              {liveBet.bets.map((result) => (
                <LiveWinCard
                  key={`win-${result.item.betId}`}
                  betDetails={result.item}
                  animationRef={result.animationRef}
                  className={isPaused ? 'stream-paused' : ''}
                />
              ))}
            </StyledFlexItems>
          </StyledFlexContainer>
        )}
      </WinContainer>
    </Box>
  )
}

const shimmer = keyframes`
  0% {
    transform: translateX(-100%);
  }
    
  100% {
    transform: translateX(100%);
  }
`

const SkeletonCard = styled(Box)`
  width: 100%;
  height: 172px;
  border-radius: ${({ theme }) => theme.radii.default};
  background: ${({ theme }) => theme.colors.backgroundAlt};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 80%;
    height: 100%;
    background: linear-gradient(90deg, transparent, ${({ theme }) => theme.colors.backgroundAlt2} 50%, transparent);
    animation: ${shimmer} 1s infinite linear;
  }
`

const WinContainer = styled(Box)`
  position: relative;
  width: 100%;
  padding: 4px 0;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  ${openEffectMixin({ openType: 'grow', delay: 0.2, duration: 0.5 })}
`

const NoWinsMessage = styled(Box)`
  text-align: center;
  padding: 24px 0;
  color: ${({ theme }) => theme.colors.textSubtle};
`

export default LiveWinStream
